import { lazy, Suspense, useEffect } from 'react';
import Hero from '../components/hero.tsx';
import { takePendingSection } from '../lib/router';
import { refreshScroll, scrollToTarget } from '../lib/smoothScroll';

// Lazy load components that are below the fold
const About = lazy(() => import('../components/about.tsx'));
const Stats = lazy(() => import('../components/stats.tsx'));
const Services = lazy(() => import('../components/services.tsx'));
const Projects = lazy(() => import('../components/projects.tsx'));
const Contact = lazy(() => import('../components/contact.tsx'));

// Loading fallback component
const LoadingFallback = () => <div style={{ minHeight: '100vh' }} />;

interface HomeProps {
    /** False while the loader covers the page; the hero's headline enters once it is true. */
    ready: boolean;
}

/*
 * The home page. Coming back from a project page, it glides to the section the visitor was heading for
 * (the sections load lazily, so it waits for that section to exist).
 */
const Home = ({ ready }: HomeProps) => {
    useEffect(() => {
        const section = takePendingSection();
        if (!section) return;
        let tries = 0;
        let timer = 0;
        let lastHeight = -1;
        let steady = 0;
        // Wait for the section to exist and for the page height to stop changing (sections and pictures load
        // late), then let the scroller re-read the height and glide.
        const attempt = () => {
            const el = document.getElementById(section);
            const height = document.documentElement.scrollHeight;
            steady = el && height === lastHeight ? steady + 1 : 0;
            lastHeight = height;
            if (el && (steady >= 3 || tries > 100)) {
                refreshScroll();
                scrollToTarget(el, 0);
                return;
            }
            if (++tries < 150) timer = window.setTimeout(attempt, 100);
        };
        attempt();
        return () => window.clearTimeout(timer);
    }, []);

    return (
        <>
            <Hero ready={ready} />
            <Suspense fallback={<LoadingFallback />}>
                <About />
                <Stats />
                <Projects />
                <Services />
                <Contact />
            </Suspense>
        </>
    );
};

export default Home;
